/**
 * ServiceProjectAssignmentsSection — the "Project Assignments" section of
 * the Service Detail page: assign-to-project form, per-assignment inline
 * edit (outcome/status/note), and remove confirmation (issue #535).
 * Extracted from `routes/services/$serviceId.tsx` (issue #1411).
 */
import { useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  useServiceAssignments,
  useProjects,
  useProjectMilestones,
  useProjectOutcomes,
  LIST_PROJECT_OUTCOMES,
} from '../../graphql/queries';
import { useProjectAssignmentNames } from '../../hooks/useProjectAssignmentNames';
import { PermissionGate, StatusBadge, DeleteConfirmationDialog } from '../shared';
import {
  createServiceAssignment,
  updateServiceAssignment,
  deleteServiceAssignment,
  ApiError,
  type CreateServiceAssignmentInput,
  type UpdateServiceAssignmentInput,
  type AssignmentStatusInput,
} from '../../lib/apiClient';
import type { ProjectOutcome, ServiceAssignment } from '../../graphql/types';

const BannerElement = 'forge-banner' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export interface ServiceProjectAssignmentsSectionProps {
  serviceId: string;
}

/** Project assignment form, list, inline edit, and remove flow (issue #535). */
export function ServiceProjectAssignmentsSection({
  serviceId,
}: ServiceProjectAssignmentsSectionProps): JSX.Element {
  const apolloClient = useApolloClient();

  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({
    projectId: '',
    milestoneId: '',
    outcomeId: '',
    statusNote: '',
  });
  const [assignError, setAssignError] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    outcomeId: string;
    assignmentStatus: AssignmentStatusInput;
    statusNote: string;
  }>({ outcomeId: '', assignmentStatus: 'planned', statusNote: '' });
  const [editOutcomes, setEditOutcomes] = useState<ProjectOutcome[]>([]);
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [removingAssignment, setRemovingAssignment] = useState<ServiceAssignment | null>(null);
  const [isRemovingAssignment, setIsRemovingAssignment] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data: assignmentsData, refetch: refetchAssignments } = useServiceAssignments(serviceId);
  const { data: projectsData } = useProjects();
  const { data: milestonesData } = useProjectMilestones(assignForm.projectId);
  const { data: outcomesData } = useProjectOutcomes(assignForm.projectId);
  const assignments = assignmentsData?.serviceAssignments ?? [];
  const allProjects = projectsData?.projects?.edges?.map((e) => e.node) ?? [];
  const availableMilestones = milestonesData?.projectMilestones ?? [];
  const availableOutcomes = outcomesData?.projectOutcomes ?? [];

  // Resolve project / milestone / outcome names for display (issue #535).
  // Project names come from the already-loaded `allProjects` list (used by
  // the assign form's project picker); milestone/outcome names require a
  // per-project batch lookup since there's no lookup-by-id query for them.
  const projectNameById: Record<string, string> = {};
  for (const proj of allProjects) {
    projectNameById[proj.entityId] = proj.name;
  }
  const { milestoneNames, outcomeNames } = useProjectAssignmentNames(assignments);

  // Open the inline edit form for an existing assignment. Milestone is
  // immutable post-create (the PUT route keys on it) — only outcome, status,
  // and note are editable; reassigning to a different milestone requires
  // remove + re-assign (issue #535).
  const handleOpenEditAssignment = (assignment: ServiceAssignment): void => {
    setEditingMilestoneId(assignment.milestoneId);
    setEditForm({
      outcomeId: assignment.outcomeId,
      assignmentStatus: assignment.assignmentStatus as AssignmentStatusInput,
      statusNote: assignment.statusNote ?? '',
    });
    setEditError(null);
    setEditOutcomes([]);
    apolloClient
      .query<{ projectOutcomes: ProjectOutcome[] }>({
        query: LIST_PROJECT_OUTCOMES,
        variables: { projectId: assignment.projectId },
      })
      .then((result) => {
        setEditOutcomes(result.data?.projectOutcomes ?? []);
      })
      .catch(() => {
        setEditOutcomes([]);
      });
  };

  const handleCancelEditAssignment = (): void => {
    setEditingMilestoneId(null);
    setEditError(null);
  };

  const handleSaveEditAssignment = (): void => {
    if (!editingMilestoneId) return;
    setIsSavingEdit(true);
    setEditError(null);
    const updates: UpdateServiceAssignmentInput = {
      outcomeId: editForm.outcomeId,
      assignmentStatus: editForm.assignmentStatus,
      statusNote: editForm.statusNote || undefined,
    };
    updateServiceAssignment(serviceId, editingMilestoneId, updates)
      .then(() => {
        setEditingMilestoneId(null);
        void refetchAssignments();
      })
      .catch((err) => {
        setEditError(err instanceof ApiError ? err.message : 'Failed to update assignment');
      })
      .finally(() => setIsSavingEdit(false));
  };

  const handleRequestRemoveAssignment = (assignment: ServiceAssignment): void => {
    setRemovingAssignment(assignment);
    setRemoveError(null);
  };

  const handleCancelRemoveAssignment = (): void => {
    setRemovingAssignment(null);
  };

  const handleConfirmRemoveAssignment = async (): Promise<void> => {
    if (!removingAssignment) return;
    setIsRemovingAssignment(true);
    try {
      await deleteServiceAssignment(serviceId, removingAssignment.milestoneId);
      setRemovingAssignment(null);
      void refetchAssignments();
    } catch (err) {
      setRemoveError(err instanceof ApiError ? err.message : 'Failed to remove assignment');
      setRemovingAssignment(null);
    } finally {
      setIsRemovingAssignment(false);
    }
  };

  return (
    <>
      {/* Gated on service:update (not a separate project:assign permission)
          because the backend authorizes this route against Service
          ownership — assertCanEdit(user, 'Service', ...) in
          projectAssignments.ts, the same rule as the Technology section
          above. See issue #535. */}
      <PermissionGate permission="service:update">
        <section
          className="project-assignments-section service-section"
          aria-labelledby="project-assignments-heading"
          data-testid="project-assignments-section"
        >
          <div className="service-section-header">
            <h2 id="project-assignments-heading" className="forge-typography--headline6">
              Project Assignments
            </h2>
            {!showAssignForm && (
              <ForgeButton
                type="button"
                variant="outlined"
                onClick={() => {
                  setShowAssignForm(true);
                  setAssignError(null);
                }}
              >
                Assign to Project
              </ForgeButton>
            )}
          </div>

          {assignError && (
            <BannerElement
              theme="error"
              dismissed={false}
              role="alert"
              className="assignment-banner"
            >
              <span>{assignError}</span>
            </BannerElement>
          )}

          {showAssignForm && (
            <div className="assignment-create-form" data-testid="assign-form">
              <div className="assignment-field">
                <label htmlFor="assign-project" className="forge-typography--caption">
                  Project
                </label>
                <select
                  id="assign-project"
                  className="filter-select"
                  value={assignForm.projectId}
                  onChange={(e) =>
                    setAssignForm((p) => ({
                      ...p,
                      projectId: e.target.value,
                      milestoneId: '',
                      outcomeId: '',
                    }))
                  }
                  aria-label="Select project"
                >
                  <option value="">— Select project —</option>
                  {allProjects.map((proj) => (
                    <option key={proj.entityId} value={proj.entityId}>
                      {proj.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assignment-field">
                <label htmlFor="assign-milestone" className="forge-typography--caption">
                  Milestone
                </label>
                <select
                  id="assign-milestone"
                  className="filter-select"
                  value={assignForm.milestoneId}
                  onChange={(e) => setAssignForm((p) => ({ ...p, milestoneId: e.target.value }))}
                  disabled={!assignForm.projectId}
                  aria-label="Select milestone"
                >
                  <option value="">— Select milestone —</option>
                  {availableMilestones.map((ms) => (
                    <option key={ms.entityId} value={ms.entityId}>
                      {ms.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assignment-field">
                <label htmlFor="assign-outcome" className="forge-typography--caption">
                  Outcome
                </label>
                <select
                  id="assign-outcome"
                  className="filter-select"
                  value={assignForm.outcomeId}
                  onChange={(e) => setAssignForm((p) => ({ ...p, outcomeId: e.target.value }))}
                  disabled={!assignForm.projectId}
                  aria-label="Select outcome"
                >
                  <option value="">— Select outcome —</option>
                  {availableOutcomes.map((oc) => (
                    <option key={oc.entityId} value={oc.entityId}>
                      {oc.name}
                    </option>
                  ))}
                </select>
              </div>

              <ForgeButton
                type="button"
                variant="raised"
                disabled={
                  isAssigning ||
                  !assignForm.projectId ||
                  !assignForm.milestoneId ||
                  !assignForm.outcomeId
                }
                onClick={() => {
                  setIsAssigning(true);
                  setAssignError(null);
                  const input: CreateServiceAssignmentInput = {
                    milestoneId: assignForm.milestoneId,
                    outcomeId: assignForm.outcomeId,
                    assignmentStatus: 'planned',
                    statusNote: assignForm.statusNote || undefined,
                  };
                  createServiceAssignment(serviceId, input)
                    .then(() => {
                      setShowAssignForm(false);
                      setAssignForm({
                        projectId: '',
                        milestoneId: '',
                        outcomeId: '',
                        statusNote: '',
                      });
                      void refetchAssignments();
                    })
                    .catch((err) => {
                      setAssignError(
                        err instanceof ApiError ? err.message : 'Failed to create assignment'
                      );
                    })
                    .finally(() => setIsAssigning(false));
                }}
              >
                Assign
              </ForgeButton>
              <ForgeButton
                type="button"
                variant="outlined"
                onClick={() => setShowAssignForm(false)}
              >
                Cancel
              </ForgeButton>
            </div>
          )}

          {removeError && (
            <BannerElement
              theme="error"
              dismissed={false}
              role="alert"
              className="assignment-banner"
              data-testid="remove-assignment-error"
            >
              <span>{removeError}</span>
            </BannerElement>
          )}

          {assignments.length === 0 && !showAssignForm && (
            <p className="forge-typography--body2 page-header-subtitle">
              Not assigned to any projects yet.
            </p>
          )}
          {assignments.map((a) => {
            const rowKey = `${a.projectId}-${a.milestoneId}`;
            const isEditingRow = editingMilestoneId === a.milestoneId;
            const projectName = projectNameById[a.projectId] ?? a.projectId;
            const milestoneName = milestoneNames[a.milestoneId] ?? a.milestoneId;
            const outcomeName = outcomeNames[a.outcomeId] ?? a.outcomeId;

            return (
              <div key={rowKey} data-testid={`assignment-${rowKey}`} className="assignment-row">
                <div className="assignment-row-main">
                  <StatusBadge value={a.assignmentStatus} variant="lifecycle" />
                  <span className="forge-typography--body2">
                    {projectName} / {milestoneName} — {outcomeName}
                  </span>
                  {a.statusNote && !isEditingRow && (
                    <span className="forge-typography--caption assignment-row-note">
                      {a.statusNote}
                    </span>
                  )}
                </div>

                {!isEditingRow && (
                  <PermissionGate permission="service:update">
                    <div className="assignment-actions">
                      <ForgeButton
                        type="button"
                        variant="text"
                        onClick={() => handleOpenEditAssignment(a)}
                        aria-label={`Edit assignment for ${milestoneName}`}
                        data-testid={`edit-assignment-${rowKey}`}
                      >
                        <forge-icon name="edit" slot="start" aria-hidden="true"></forge-icon>
                        Edit
                      </ForgeButton>
                      <ForgeButton
                        type="button"
                        variant="text"
                        onClick={() => handleRequestRemoveAssignment(a)}
                        aria-label={`Remove assignment for ${milestoneName}`}
                        data-testid={`remove-assignment-${rowKey}`}
                      >
                        <forge-icon name="close" slot="start" aria-hidden="true"></forge-icon>
                        Remove
                      </ForgeButton>
                    </div>
                  </PermissionGate>
                )}

                {isEditingRow && (
                  <div
                    className="assignment-edit-form"
                    data-testid={`edit-assignment-form-${rowKey}`}
                  >
                    {editError && (
                      <BannerElement
                        theme="error"
                        dismissed={false}
                        role="alert"
                        className="assignment-banner"
                        data-testid="edit-assignment-error"
                      >
                        <span>{editError}</span>
                      </BannerElement>
                    )}

                    <div className="assignment-field">
                      <label
                        htmlFor={`edit-outcome-${rowKey}`}
                        className="forge-typography--caption"
                      >
                        Outcome
                      </label>
                      <select
                        id={`edit-outcome-${rowKey}`}
                        className="filter-select"
                        value={editForm.outcomeId}
                        onChange={(e) => setEditForm((p) => ({ ...p, outcomeId: e.target.value }))}
                        aria-label="Edit outcome"
                      >
                        <option value="">— Select outcome —</option>
                        {editOutcomes.map((oc) => (
                          <option key={oc.entityId} value={oc.entityId}>
                            {oc.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="assignment-field">
                      <label
                        htmlFor={`edit-status-${rowKey}`}
                        className="forge-typography--caption"
                      >
                        Status
                      </label>
                      <select
                        id={`edit-status-${rowKey}`}
                        className="filter-select"
                        value={editForm.assignmentStatus}
                        onChange={(e) =>
                          setEditForm((p) => ({
                            ...p,
                            assignmentStatus: e.target.value as AssignmentStatusInput,
                          }))
                        }
                        aria-label="Edit status"
                      >
                        <option value="planned">Planned</option>
                        <option value="in-progress">In Progress</option>
                        <option value="done">Done</option>
                      </select>
                    </div>

                    <div className="assignment-field">
                      <label htmlFor={`edit-note-${rowKey}`} className="forge-typography--caption">
                        Status note
                      </label>
                      <input
                        id={`edit-note-${rowKey}`}
                        className="search-input"
                        value={editForm.statusNote}
                        onChange={(e) => setEditForm((p) => ({ ...p, statusNote: e.target.value }))}
                        aria-label="Edit status note"
                      />
                    </div>

                    <div className="assignment-actions">
                      <ForgeButton
                        type="button"
                        variant="raised"
                        disabled={isSavingEdit || !editForm.outcomeId}
                        onClick={handleSaveEditAssignment}
                        data-testid={`save-assignment-${rowKey}`}
                      >
                        Save
                      </ForgeButton>
                      <ForgeButton
                        type="button"
                        variant="outlined"
                        onClick={handleCancelEditAssignment}
                        data-testid={`cancel-edit-assignment-${rowKey}`}
                      >
                        Cancel
                      </ForgeButton>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </PermissionGate>

      {/* Remove Assignment Confirmation Dialog (issue #535).
          Mounted only while a removal is pending so the service detail page
          never has two forge-dialog elements in the DOM at once (a second
          always-mounted dialog breaks strict-mode `forge-dialog` locators). */}
      {removingAssignment !== null && (
        <DeleteConfirmationDialog
          open
          title="Remove Assignment"
          message="Are you sure you want to remove this project assignment?"
          itemName={`${
            projectNameById[removingAssignment.projectId] ?? removingAssignment.projectId
          } / ${milestoneNames[removingAssignment.milestoneId] ?? removingAssignment.milestoneId}`}
          onConfirm={() => void handleConfirmRemoveAssignment()}
          onCancel={handleCancelRemoveAssignment}
          isDeleting={isRemovingAssignment}
        />
      )}
    </>
  );
}
